import useLocalStorage from '../../common/hook/useLocalStorage';
import useLocalStorageMap from '../../common/hook/useLocalStorageMap';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {AUTO_SCROLL_SPEED_OPTION_KEY} from '../../viewer/container/Setting';
import {IViewType} from '../../viewer/type';

export default function useViewerSettings(
  contentId?: number
): [settings: ISetting, setSetting: ISetter] {
  const defaultViewType = useTypedSelector(
    ({viewer}) => viewer.content?.defaultViewType ?? 'scroll'
  );

  const [isPreviewComment, setIsPreviewComment] = useLocalStorage<boolean>({
    key: 'viewer-isPreviewComment',
    initialValue: DEFAULT_VIEWER_SETTING.isPreviewComment,
  });
  const [isKeepLastPage, setIsKeepLastPage] = useLocalStorage<boolean>({
    key: 'viewer-isKeepLastPage',
    initialValue: DEFAULT_VIEWER_SETTING.isKeepLastPage,
  });
  const [isAutoScroll, setIsAutoScroll] = useLocalStorage<boolean>({
    key: 'viewer-isAutoScroll',
    initialValue: DEFAULT_VIEWER_SETTING.isAutoScroll,
  });
  const [autoScrollSpeed, setAutoScrollSpeed] = useLocalStorage<number>({
    key: AUTO_SCROLL_SPEED_OPTION_KEY,
    initialValue: DEFAULT_VIEWER_SETTING.autoScrollSpeed,
  });
  const [viewTypeMap, mergeViewType] = useLocalStorageMap<IViewType>({
    key: 'viewer-view-type',
  });

  const setSetting: ISetter = (key, value) => {
    switch (key) {
      case 'isPreviewComment':
        setIsPreviewComment(value as ISetting['isPreviewComment']);
        break;
      case 'isKeepLastPage':
        setIsKeepLastPage(value as ISetting['isKeepLastPage']);
        break;
      case 'isAutoScroll':
        setIsAutoScroll(value as ISetting['isAutoScroll']);
        break;
      case 'autoScrollSpeed':
        setAutoScrollSpeed(value as ISetting['autoScrollSpeed']);
        break;
      case 'viewType':
        if (contentId !== undefined)
          mergeViewType({[contentId]: value as ISetting['viewType']});
    }
  };

  return [
    {
      isPreviewComment,
      isKeepLastPage,
      isAutoScroll,
      autoScrollSpeed,
      viewType:
        contentId !== undefined && viewTypeMap[contentId]
          ? viewTypeMap[contentId]
          : defaultViewType,
    },
    setSetting,
  ];
}

type ISetter = <T extends keyof ISetting>(key: T, value: ISetting[T]) => void;

interface ISetting {
  isPreviewComment: boolean;
  isKeepLastPage: boolean;
  isAutoScroll: boolean;
  autoScrollSpeed: number;
  viewType: IViewType;
}

export const DEFAULT_VIEWER_SETTING: {
  [key in keyof Omit<ISetting, 'viewType'>]: ISetting[key];
} = {
  isPreviewComment: true,
  isKeepLastPage: true,
  isAutoScroll: true,
  autoScrollSpeed: 1,
};
